/* eslint-disable react/no-unescaped-entities */
import { Link } from "react-router-dom";
import { ArrowRight, Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { useLang, WHATSAPP_NUMBER, WHATSAPP_DISPLAY } from "@/lib/i18n";

export default function ContactCTA() {
  const { t, lang } = useLang();

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-[#1C3A27] text-white relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -bottom-32 -left-32 w-[480px] h-[480px] rounded-full opacity-[0.07] bg-[radial-gradient(#FAFAF7_1px,transparent_1px)] [background-size:14px_14px]"
      />
      <div className="max-w-5xl mx-auto px-6 md:px-12 relative">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-sm font-bold uppercase tracking-[0.2em] text-[#E05A3D]">
            {t.contact.overline}
          </div>
          <h2 className="mt-4 font-display text-4xl md:text-5xl font-black tracking-tight text-white">
            {t.contact.headline}
          </h2>
          <p className="mt-6 text-lg text-white/75 leading-relaxed">
            {t.contact.description}
          </p>

          <div
            data-testid="cta-signup-actions"
            className="mt-8 flex flex-col sm:flex-row gap-3 justify-center"
          >
            <Link
              to="/signup"
              data-testid="cta-signup-link"
              className="inline-flex items-center justify-center h-14 rounded-2xl bg-[#E05A3D] hover:bg-[#C84B31] text-white font-bold px-6 group transition-colors"
            >
              {t.contact.signup_cta}
              <ArrowRight
                size={18}
                className={`ms-2 group-hover:translate-x-1 transition ${
                  lang === "ar" ? "rotate-180" : ""
                }`}
              />
            </Link>
            <Link
              to="/login"
              data-testid="cta-login-link"
              className="inline-flex items-center justify-center h-14 rounded-2xl bg-white/10 hover:bg-white/15 border border-white/20 text-white font-semibold px-6 transition-colors"
            >
              {t.contact.login_cta}
            </Link>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 gap-3.5 text-start max-w-2xl mx-auto">
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-link"
              className="sm:col-span-2 group flex items-center gap-3 p-4 rounded-2xl bg-[#25D366]/15 hover:bg-[#25D366]/25 border border-[#25D366]/30 transition-colors"
            >
              <div className="w-10 h-10 rounded-xl bg-[#25D366] grid place-items-center shrink-0">
                <MessageCircle size={18} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold">
                  WhatsApp — <span>{t.trainer.school}</span>
                </div>
                <div className="text-white font-semibold text-base" dir="ltr">
                  {WHATSAPP_DISPLAY}
                </div>
              </div>
              <ArrowRight
                size={18}
                className={`text-white/70 group-hover:translate-x-1 transition ${
                  lang === "ar" ? "rotate-180" : ""
                }`}
              />
            </a>
            <div className="flex items-center gap-3 text-white/80 p-3 rounded-xl bg-white/5">
              <Mail size={18} className="text-[#E05A3D] flex-shrink-0" />
              <span dir="ltr" className="text-sm truncate">{t.contact.email}</span>
            </div>
            <div className="flex items-center gap-3 text-white/80 p-3 rounded-xl bg-white/5">
              <Phone size={18} className="text-[#E05A3D] flex-shrink-0" />
              <span dir="ltr" className="text-sm">{t.contact.phone}</span>
            </div>
            <div className="sm:col-span-2 flex items-center gap-3 text-white/80 p-3 rounded-xl bg-white/5">
              <MapPin size={18} className="text-[#E05A3D] flex-shrink-0" />
              <span className="text-sm">{t.contact.address}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
